import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Select, Input, Button, Spin, AutoComplete } from 'antd';
import { Prospect } from 'types/common';
import useMyAllProspectList from 'hooks/useMyAllProspectList';
import useSettings from 'hooks/useSettings';
import useLinkableVisits from 'hooks/useLinkableVisits';
import useTimezone from 'hooks/useTimezone';
import dayjs from 'utils/dayjs';
import { EnumFields } from 'types/enumerationData';
import { createStandaloneNote } from 'api/treatment';
import s from './s.module.less';

const { Option } = Select;

export type CreateVisitNoteResult = {
    itemType: 'APPOINTMENT' | 'TEXT_VISIT' | 'STANDALONE';
    appointmentId?: number;
    textVisitUuid?: string;
    providerTreatmentSubmitted?: boolean;
    specialty?: string;
    clientId?: number;
    standaloneNoteId?: number;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onContinue?: (result: CreateVisitNoteResult) => void;
    prospect?: Prospect;
};

const CreateVisitNoteModal = ({ open, onClose, onContinue, prospect }: Props) => {
    const [creating, setCreating] = useState(false);
    const [selectedVisitKey, setSelectedVisitKey] = useState<string | undefined>(undefined);
    const [selectedSpecialty, setSelectedSpecialty] = useState<string | undefined>(undefined);
    const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
    const [specialtySearch, setSpecialtySearch] = useState('');

    const hasProspectProp = !!prospect;
    const [allProspects] = useMyAllProspectList();
    const [setting] = useSettings();

    const activeClientId = hasProspectProp ? prospect?.id : selectedClientId;
    const [visitOptions, visitLoading] = useLinkableVisits(activeClientId, open);
    const [, myTimezone] = useTimezone();

    const selectOptions = React.useMemo(() => {
        const tz = myTimezone?.value as string;
        return visitOptions.map((opt) => {
            if (opt.itemType === 'APPOINTMENT' && opt.startAt) {
                const start = dayjs(opt.startAt).tz(tz);
                const end = opt.endAt ? dayjs(opt.endAt).tz(tz) : null;
                const dateLine = start.format('dddd, MMMM D, YYYY');
                const timeLine = end
                    ? `${start.format('h:mm')} - ${end.format('h:mm a')} (${tz})${opt.duration ? ` | ${opt.duration} mins` : ''}`
                    : start.format('h:mm a');
                return {
                    value: opt.value,
                    searchText: opt.label,
                    selectedLabel: `${opt.label} - ${dateLine}`,
                    title: opt.label,
                    dateLine,
                    timeLine,
                    itemType: opt.itemType,
                };
            }
            if (opt.itemType === 'TEXT_VISIT') {
                const parts: string[] = [];
                if (opt.patientSubmitAt) parts.push(`Refill on ${dayjs(opt.patientSubmitAt).format('MMM D')}`);
                if (opt.patientSubmitAt) parts.push(`Submitted on ${dayjs(opt.patientSubmitAt).format('MMM D')}`);
                if (opt.updatedAt) parts.push(`Last updated on ${dayjs(opt.updatedAt).format('MMM D')}`);
                const subLine = parts.join(' | ');
                return {
                    value: opt.value,
                    searchText: opt.label,
                    selectedLabel: subLine ? `${opt.label} - ${subLine}` : opt.label,
                    title: opt.label,
                    dateLine: subLine,
                    timeLine: '',
                    itemType: opt.itemType,
                };
            }
            return { value: opt.value, searchText: opt.label, selectedLabel: opt.label, title: opt.label, dateLine: '', timeLine: '', itemType: opt.itemType };
        });
    }, [visitOptions, myTimezone]);

    const specialtyOptions = React.useMemo(() => {
        const dict = setting[EnumFields.HELLO_KLARITY_SPECIALTY] || [];
        const sorted = [...dict].sort((a, b) => {
            const aLabel = String(a.label || '').toLowerCase();
            const bLabel = String(b.label || '').toLowerCase();
            if (['other', 'others'].includes(aLabel)) return 1;
            if (['other', 'others'].includes(bLabel)) return -1;
            return aLabel.localeCompare(bLabel);
        });
        return sorted
            .filter((o) => {
                if (!specialtySearch) return true;
                return String(o.label || '').toLowerCase().includes(specialtySearch.toLowerCase());
            })
            .map((o) => ({ label: String(o.label || ''), value: String(o.label || '') }));
    }, [setting, specialtySearch]);

    const clientName = React.useMemo(() => {
        const p = hasProspectProp ? prospect : allProspects.find((item) => item.id === selectedClientId);
        return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
    }, [hasProspectProp, prospect, allProspects, selectedClientId]);

    useEffect(() => {
        if (!open) {
            setSelectedVisitKey(undefined);
            setSelectedSpecialty(undefined);
            setSelectedClientId(undefined);
            setSpecialtySearch('');
        }
    }, [open]);

    const handleClientChange = useCallback((val: number) => {
        setSelectedClientId(val);
        setSelectedVisitKey(undefined);
        setSelectedSpecialty(undefined);
    }, []);

    const hasVisitItems = visitOptions.length > 0;

    const handleVisitChange = useCallback((val: string | undefined) => {
        setSelectedVisitKey(val);
    }, []);

    const handleSpecialtyChange = useCallback((val: string) => {
        setSelectedSpecialty(val);
    }, []);

    const clientOptions = allProspects.map((p) => ({
        label: `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        value: p.id as number,
    }));

    const handleContinue = useCallback(async () => {
        if (selectedVisitKey && onContinue) {
            const selectedOption = visitOptions.find((opt) => opt.value === selectedVisitKey);

            if (selectedOption) {
                onContinue({
                    itemType: selectedOption.itemType,
                    appointmentId: selectedOption.appointmentId,
                    textVisitUuid: selectedOption.textVisitUuid,
                    providerTreatmentSubmitted: selectedOption.providerTreatmentSubmitted,
                    specialty: selectedSpecialty,
                    clientId: activeClientId,
                });
            }
            onClose();
        } else if (onContinue && activeClientId) {
            setCreating(true);
            try {
                const res = await createStandaloneNote({
                    clientId: activeClientId,
                    specialty: selectedSpecialty || undefined,
                });
                if (res && !res.error) {
                    const noteData = res.data?.data;
                    onContinue({
                        itemType: 'STANDALONE',
                        standaloneNoteId: noteData?.id,
                        specialty: selectedSpecialty,
                        clientId: activeClientId,
                    });
                    onClose();
                }
            } catch (e) {
                console.error(e);
            } finally {
                setCreating(false);
            }
        }
    }, [onClose, onContinue, selectedVisitKey, selectedSpecialty, activeClientId, visitOptions]);

    return (
        <Modal
            open={open}
            title="Create visit note"
            onCancel={onClose}
            width={543}
            maskClosable={false}
            keyboard={false}
            destroyOnClose
            footer={
                <div className={s.footer}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleContinue} disabled={!activeClientId} loading={creating}>Continue</Button>
                </div>
            }
        >
            <Spin spinning={visitLoading}>
                <div className={s.content}>
                    <div className={s.field}>
                        <div className={s.label}>Client</div>
                        {hasProspectProp ? (
                            <Input value={clientName} readOnly className={s.readOnlyInput} />
                        ) : (
                            <Select
                                placeholder="Search or select a client"
                                value={selectedClientId}
                                onChange={handleClientChange}
                                options={clientOptions}
                                showSearch
                                optionFilterProp="label"
                                className={s.select}
                            />
                        )}
                    </div>
                    <div className={s.description}>
                        Choose a specialty, or link this note to a scheduled appointment. Already sent a note for this visit? Add an addendum instead.
                    </div>
                    <div className={s.field}>
                        <div className={s.label}>Link to visit <span className={s.optional}>(optional)</span></div>
                        <Select
                            placeholder="Select a visit"
                            value={selectedVisitKey}
                            onChange={handleVisitChange}
                            allowClear
                            disabled={!activeClientId || !hasVisitItems}
                            showSearch
                            optionLabelProp="label"
                            filterOption={(input, option) =>
                                (option?.searchText || '').toLowerCase().includes(input.toLowerCase())
                            }
                            className={s.select}
                            listHeight={400}
                        >
                            {selectOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value} label={opt.selectedLabel} searchText={opt.searchText}>
                                    <div>
                                        <div>{opt.title}</div>
                                        {opt.dateLine && <div style={{ color: '#6B7280', fontSize: 12 }}>{opt.dateLine}</div>}
                                        {opt.timeLine && <div style={{ color: '#6B7280', fontSize: 12 }}>{opt.timeLine}</div>}
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div className={s.field}>
                        <div className={s.label}>Specialty <span className={s.optional}>(optional)</span></div>
                        <AutoComplete
                            placeholder="Select or type a specialty"
                            value={selectedSpecialty}
                            onChange={handleSpecialtyChange}
                            onSearch={setSpecialtySearch}
                            options={specialtyOptions}
                            allowClear
                            disabled={!activeClientId}
                            className={s.select}
                        />
                    </div>
                </div>
            </Spin>
        </Modal>
    );
};

export default CreateVisitNoteModal;
